// API配置和工具函数
import { processImageUrls } from '../config/imageConfig';

// API基础配置
const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

// 请求拦截器
class ApiClient {
  constructor(config = {}) {
    this.config = { ...API_CONFIG, ...config };
    this.token = localStorage.getItem('auth_token');
  }

  // 设置认证token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  // 获取请求头
  getHeaders(customHeaders = {}) {
    const headers = { ...this.config.headers, ...customHeaders };
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    return headers;
  }

  // 处理响应
  async handleResponse(response) {
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        code: response.status,
        message: response.statusText
      }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
    const data = await response.json();
    
    // 自动处理响应数据中的图片URL
    if (data && typeof data === 'object') {
      return processImageUrls(data);
    }
    
    return data;
  }

  // GET请求
  async get(endpoint, params = {}) {
    let url;
    const fullUrl = `${this.config.baseURL}${endpoint}`;
    
    // 如果是相对路径，使用当前域名构造完整URL
    if (this.config.baseURL.startsWith('/')) {
      url = new URL(fullUrl, window.location.origin);
    } else {
      url = new URL(fullUrl);
    }
    
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error(`GET ${endpoint} failed:`, error);
      throw error;
    }
  }

  // POST请求
  async post(endpoint, data = {}, customHeaders = {}) {
    try {
      const response = await fetch(`${this.config.baseURL}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(customHeaders),
        body: JSON.stringify(data),
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error(`POST ${endpoint} failed:`, error);
      throw error;
    }
  }

  // PUT请求
  async put(endpoint, data = {}, customHeaders = {}) {
    try {
      const response = await fetch(`${this.config.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(customHeaders),
        body: JSON.stringify(data),
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error(`PUT ${endpoint} failed:`, error);
      throw error;
    }
  }

  // DELETE请求
  async delete(endpoint, customHeaders = {}) {
    try {
      const response = await fetch(`${this.config.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(customHeaders),
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error(`DELETE ${endpoint} failed:`, error);
      throw error;
    }
  }

  // 文件上传
  async upload(endpoint, file, additionalData = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    try {
      const headers = {};
      if (this.token) {
        headers.Authorization = `Bearer ${this.token}`;
      }

      const response = await fetch(`${this.config.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error(`Upload to ${endpoint} failed:`, error);
      throw error;
    }
  }
}

// 创建API客户端实例
const apiClient = new ApiClient();

// 植物相关API
export const plantsApi = {
  // 获取植物列表
  getPlants: (params = {}) => apiClient.get('/plants', params),
  
  // 获取植物详情
  getPlantDetail: (id) => apiClient.get(`/plants/${id}`),
  
  // 获取植物分类
  getPlantCategories: () => apiClient.get('/plants/categories'),
  
  // 搜索植物
  searchPlants: (keyword, filters = {}) => apiClient.get('/plants/search', { keyword, ...filters }),
  
  // 获取植物特色展示
  getFeaturedPlants: () => apiClient.get('/plants/featured'),
  
  // 获取植物保护计划
  getConservationPlans: () => apiClient.get('/plants/conservation'),
  
  // 获取濒危植物名录
  getEndangeredPlants: () => apiClient.get('/plants/endangered')
};

// 活动相关API - 符合门户网站活动API规范
export const activityApi = {
  // 1. 活动分类接口
  // 获取活动分类列表
  getCategories: () => apiClient.get('/portal/activity/categories'),
  
  // 2. 活动列表接口
  // 获取活动列表（分页）
  getList: (params = {}) => apiClient.get('/portal/activity/list', params),
  
  // 获取精选活动
  getFeatured: (limit = 6) => apiClient.get('/portal/activity/featured', { limit }),
  
  // 获取即将开始的活动
  getUpcoming: (params = {}) => apiClient.get('/portal/activity/upcoming', {
    days: params.days || 7,
    limit: params.limit || 10
  }),
  
  // 3. 活动详情接口
  // 获取活动详情
  getDetail: (id) => apiClient.get(`/portal/activity/${id}`),
  
  // 增加活动浏览量
  addView: (id) => apiClient.post(`/portal/activity/${id}/view`),
  
  // 4. 活动报名接口
  // 提交活动报名
  register: (id, registrationData) => apiClient.post(`/portal/activity/${id}/register`, registrationData),
  
  // 查询报名状态
  getRegistrationStatus: (registrationId) => apiClient.get(`/portal/activity/registration/${registrationId}`),
  
  // 取消报名
  cancelRegistration: (registrationId, reason) => apiClient.post(`/portal/activity/registration/${registrationId}/cancel`, { reason }),
  
  // 5. 活动日历接口
  // 获取活动日历
  getCalendar: (year, month, categoryId = null) => {
    const params = { year, month };
    if (categoryId) params.categoryId = categoryId;
    return apiClient.get('/portal/activity/calendar', params);
  },
  
  // 6. 活动搜索接口
  // 搜索活动
  search: (params = {}) => apiClient.get('/portal/activity/search', params),
  
  // 7. 活动统计接口
  // 获取活动统计信息
  getStatistics: () => apiClient.get('/portal/activity/statistics'),
  
  // 8. 相关推荐接口
  // 获取相关活动推荐
  getRelated: (id, limit = 4) => apiClient.get(`/portal/activity/${id}/related`, { limit })
};

// 保留原有eventsApi用于兼容，逐步迁移到activityApi
export const eventsApi = {
  // 获取活动列表 - 映射到新API
  getEvents: (params = {}) => activityApi.getList(params),
  
  // 获取活动详情 - 映射到新API
  getEventDetail: (id) => activityApi.getDetail(id),
  
  // 获取活动分类 - 映射到新API
  getEventCategories: () => activityApi.getCategories(),
  
  // 获取活动日历 - 映射到新API
  getEventCalendar: (year, month) => activityApi.getCalendar(year, month),
  
  // 获取展览列表 - 暂时保留，后续单独处理
  getExhibitions: (params = {}) => apiClient.get('/exhibitions', params),
  
  // 获取展览详情 - 暂时保留，后续单独处理
  getExhibitionDetail: (id) => apiClient.get(`/exhibitions/${id}`),
  
  // 活动报名 - 映射到新API
  registerEvent: (eventId, registrationData) => activityApi.register(eventId, registrationData),
  
  // 获取报名状态 - 映射到新API
  getRegistrationStatus: (eventId, registrationId) => activityApi.getRegistrationStatus(registrationId)
};

// 商品相关API
export const productsApi = {
  // 获取商品列表
  getProducts: (params = {}) => apiClient.get('/products', params),
  
  // 获取首页产品列表
  getHomepageProducts: (params = {}) => apiClient.get('/products/homepage', params),
  
  // 获取商品详情
  getProductDetail: (id) => apiClient.get(`/products/${id}`),
  
  // 获取商品分类
  getProductCategories: () => apiClient.get('/products/categories'),
  
  // 搜索商品
  searchProducts: (keyword, filters = {}) => apiClient.get('/products/search', { keyword, ...filters }),
  
  // 获取库存信息
  getInventory: (productId) => apiClient.get(`/products/${productId}/inventory`),
  
  // 获取商品特色标签
  getProductTags: () => apiClient.get('/products/tags')
};

// 购物车相关API
export const cartApi = {
  // 添加商品到购物车
  addToCart: (productId, quantity = 1) => apiClient.post('/cart/add', { product_id: productId, quantity }),
  
  // 获取购物车列表
  getCart: () => apiClient.get('/cart'),
  
  // 更新购物车商品数量
  updateCartItem: (itemId, quantity) => apiClient.put(`/cart/items/${itemId}`, { quantity }),
  
  // 删除购物车商品
  removeCartItem: (itemId) => apiClient.delete(`/cart/items/${itemId}`),
  
  // 清空购物车
  clearCart: () => apiClient.delete('/cart')
};

// 用户相关API
export const usersApi = {
  // 用户注册
  register: (userData) => apiClient.post('/users/register', userData),
  
  // 用户登录
  login: (credentials) => apiClient.post('/users/login', credentials),
  
  // 获取用户信息
  getProfile: () => apiClient.get('/users/profile'),
  
  // 更新用户信息
  updateProfile: (userData) => apiClient.put('/users/profile', userData),
  
  // 修改密码
  changePassword: (passwordData) => apiClient.put('/users/password', passwordData),
  
  // 用户登出
  logout: () => {
    apiClient.setToken(null);
    return Promise.resolve({ code: 200, message: '登出成功' });
  }
};

// 预约相关API
export const bookingsApi = {
  // 创建参观预约
  createBooking: (bookingData) => apiClient.post('/bookings', bookingData),
  
  // 获取预约状态
  getBookingStatus: (bookingId) => apiClient.get(`/bookings/${bookingId}`),
  
  // 获取可预约时间
  getAvailableTimes: (date, routeId) => apiClient.get('/bookings/available-times', { date, route_id: routeId }),
  
  // 获取参观路线
  getRoutes: () => apiClient.get('/routes'),
  
  // 获取导游信息
  getGuides: (params = {}) => apiClient.get('/guides', params)
};

// 内容相关API
export const contentApi = {
  // 获取文章列表
  getArticles: (params = {}) => apiClient.get('/articles', params),
  
  // 获取文章详情
  getArticleDetail: (id) => apiClient.get(`/articles/${id}`),
  
  // 获取文章分类
  getArticleCategories: () => apiClient.get('/articles/categories')
};

// 新闻相关API
export const newsApi = {
  // 按分类获取新闻列表
  getNewsByCategory: (categorySlug, params = {}) => apiClient.get(`/news/category/${categorySlug}`, params),
  
  // 获取新闻详情
  getNewsDetail: (id) => apiClient.get(`/news/${id}`),
  
  // 获取新闻分类
  getNewsCategories: () => apiClient.get('/news/categories'),
  
  // 获取新闻列表
  getNews: (params = {}) => apiClient.get('/news', params)
};

// 学习教育相关API
export const educationApi = {
  // 获取课程列表
  getCourses: (params = {}) => apiClient.get('/courses', params),
  
  // 获取课程详情
  getCourseDetail: (id) => apiClient.get(`/courses/${id}`),
  
  // 课程报名
  registerCourse: (courseId, registrationData) => apiClient.post(`/courses/${courseId}/register`, registrationData),
  
  // 获取学校项目
  getSchoolPrograms: (params = {}) => apiClient.get('/school-programs', params),
  
  // 获取出版物
  getPublications: (params = {}) => apiClient.get('/publications', params)
};

// 系统相关API
export const systemApi = {
  // 获取系统配置
  getConfig: (keys) => apiClient.get('/system/config', { keys }),
  
  // 获取营业时间
  getBusinessHours: () => apiClient.get('/system/business-hours'),
  
  // 获取联系方式
  getContactInfo: () => apiClient.get('/system/contact-info'),
  
  // 记录搜索日志
  logSearch: (searchData) => apiClient.post('/search/log', searchData)
};

// 文件相关API
export const filesApi = {
  // 上传文件
  uploadFile: (file, metadata = {}) => apiClient.upload('/files/upload', file, metadata),
  
  // 获取文件列表
  getFiles: (params = {}) => apiClient.get('/files', params),
  
  // 删除文件
  deleteFile: (id) => apiClient.delete(`/files/${id}`),
  
  // 获取文件详情
  getFileDetail: (id) => apiClient.get(`/files/${id}`)
};

// 花园模块API
export const gardenApi = {
  // 获取园区区域列表
  getAreas: (params = {}) => apiClient.get('/garden/areas', params),
  
  // 获取园区区域详情
  getAreaDetail: (id) => apiClient.get(`/garden/areas/${id}`),
  
  // 获取研究项目列表
  getProjects: (params = {}) => apiClient.get('/garden/research-projects', params),
  
  // 获取研究项目详情
  getProjectDetail: (id) => apiClient.get(`/garden/research-projects/${id}`)
};

// 关于我们模块API
export const aboutApi = {
  // 获取机构信息
  getOrganization: () => apiClient.get('/about/organization'),
  
  // 获取团队成员列表
  getTeam: (params = {}) => apiClient.get('/about/team', params),
  
  // 获取团队成员详情
  getTeamMember: (id) => apiClient.get(`/about/team/${id}`),
  
  // 获取机构历史
  getHistory: (params = {}) => apiClient.get('/about/history', params)
};

// 联系我们模块API
export const contactApi = {
  // 获取联系信息
  getInfo: () => apiClient.get('/contact/info'),
  
  // 提交联系表单
  submit: (data) => apiClient.post('/contact/submit', data),
  
  // 获取咨询类型
  getInquiryTypes: () => apiClient.get('/contact/inquiry-types')
};

// 主页模块API
export const homeApi = {
  // 获取主页配置
  getConfig: () => apiClient.get('/home/config'),
  
  // 获取轮播图
  getCarousel: (categoryNameEn = 'HomePageBanner') => apiClient.get('/home/carousel', { categoryNameEn }),
  
  // 获取统计数据
  getStatistics: () => apiClient.get('/home/statistics'),
  
  // 获取推荐内容
  getFeaturedContent: (params = {}) => apiClient.get('/home/featured-content', params)
};

// 导出API客户端
export { apiClient };

// 导出默认配置
const apiServices = {
  plantsApi,
  eventsApi,
  activityApi,
  productsApi,
  cartApi,
  usersApi,
  bookingsApi,
  contentApi,
  newsApi,
  educationApi,
  systemApi,
  filesApi,
  gardenApi,
  aboutApi,
  contactApi,
  homeApi,
  apiClient
};

export default apiServices;
